import React from "react";
import { TopRatedBadge } from "@/components/TopRatedBadge";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarDays, MapPin, Moon, MessageCircle } from "lucide-react";
import { imgSrc } from "@/lib/img";
import { fmtDateTime, fmtTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import { HeartButton } from "@/components/HeartButton";
import type { AppEvent, Price } from "@/types";

const BADGE: Record<string, string> = {
  open: "bg-emerald-500 text-white",
  closed: "bg-zinc-700 text-white",
  ended: "bg-zinc-500 text-white",
  cancelled: "bg-rose-600 text-white",
};

export const evTitle = (e: AppEvent, lang: string) => (lang === "ar" ? e.title_ar : e.title_en || e.title_ar);

export const fmtMoney = (p: Price) => `${Number(p.amount).toLocaleString("en-US", { minimumFractionDigits: p.currency === "USD" ? 2 : 0, maximumFractionDigits: p.currency === "USD" ? 2 : 0 })} ${p.currency}`;

export const PriceTag = ({ e, className = "" }: { e: AppEvent; className?: string }) => {
  const { t } = usePrefs();
  if (e.is_free) return <span className={className}>{t("ev.free")}</span>;
  if (e.matched_price) return <span className={className} dir="auto" data-testid="event-matched-price">{(e.options?.length || 0) > 1 ? `${t("b4.matched")} ` : ""}{fmtMoney(e.matched_price)}</span>;
  return (
    <span className={className} dir="auto">
      {e.price_from.length && (e.options?.length || 0) > 1 ? `${t("ev.from")} ` : ""}
      {e.price_from.map(fmtMoney).join(" / ")}
    </span>
  );
};

export const StatusBadge = ({ status, className = "" }: { status: string; className?: string }) => {
  const { t } = usePrefs();
  return <span data-testid="event-status-badge" className={`rounded-full px-3 py-1 text-xs font-bold shadow ${BADGE[status]} ${className}`}>{t(`ev.${status}Badge`)}</span>;
};

export const EventCard = ({ e, index = 0, share = false }: { e: AppEvent; index?: number; share?: boolean }) => {
  const { t, lang } = usePrefs();
  const venue = lang === "ar" ? e.venue.name_ar : e.venue.name_en || e.venue.name_ar;
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 8) * 0.06, duration: 0.45 }}
      data-testid={`event-card-${e.event_id}`}
      className="group relative overflow-hidden rounded-[1.75rem] bg-card shadow-sm hover:shadow-xl transition-shadow"
    >
      <Link to={`/events/${e.event_id}`} className="block" data-testid={`event-card-link-${e.event_id}`}>
        <div className="relative aspect-[4/5] overflow-hidden">
          {e.poster || e.venue.image ? <img src={imgSrc(e.poster || e.venue.image || "")} alt={evTitle(e, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            : <div className="h-full w-full grid place-items-center bg-gradient-to-br from-primary/30 to-secondary/30"><CalendarDays className="h-14 w-14 text-white/70" /></div>}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
          <span className="absolute top-3 start-3 rounded-full bg-white/90 text-zinc-900 px-3 py-1 text-xs font-bold">{lang === "ar" ? e.type_name_ar : e.type_name_en}</span>
          <StatusBadge status={e.request_status} className="absolute top-12 start-3" />
          <HeartButton eventId={e.event_id} className="absolute top-3 end-3" />
          <div className="absolute bottom-0 inset-x-0 p-4 text-white space-y-2">
            {e.top_rated && <TopRatedBadge rating={e.venue_rating} testId={`event-top-rated-${e.event_id}`} />}
            <h3 className="font-display text-xl font-black leading-tight">{evTitle(e, lang)}</h3>
            <p className="text-sm text-white/80 line-clamp-2">{lang === "ar" ? e.short_ar : e.short_en || e.short_ar}</p>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-white/90">
              <span className="inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" />{fmtDateTime(e.start_at, lang, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              {e.ends_next_day && <span className="inline-flex items-center gap-1 text-amber-300"><Moon className="h-3.5 w-3.5" />{fmtTime(e.end_at, lang)}</span>}
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{venue}{e.area ? ` · ${lang === "ar" ? e.area : e.area_en || e.area}` : ""}</span>
            </div>
            <div className="flex items-center justify-between pt-1">
              <PriceTag e={e} className="rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-sm font-black" />
              <span className="text-xs font-bold underline underline-offset-4">{t("ev.details")}</span>
            </div>
          </div>
        </div>
      </Link>
      {share && (
        <a href={`https://wa.me/?text=${encodeURIComponent(t("share.event").replace("{title}", evTitle(e, lang)).replace("{date}", fmtDateTime(e.start_at, lang, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })) + " " + `${window.location.origin}/api/share/events/${e.event_id}`)}`}
          target="_blank" rel="noopener noreferrer" data-testid={`event-card-share-${e.event_id}`} aria-label={t("pe.share")}
          className="absolute top-3 end-14 grid h-10 w-10 place-items-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform hover:scale-110 active:scale-95">
          <MessageCircle className="h-5 w-5" />
        </a>
      )}
    </motion.article>
  );
};
